import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api, formatApiError as formatErr } from "../lib/api";
import { PageHero } from "../components/shared/Primitives";

export default function CareDiscussionRoomNew() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const linkedPlacementId = searchParams.get("placement_id") || "";
  const [form, setForm] = useState({
    client_label: searchParams.get("client_label") || "",
    carer_name: searchParams.get("carer_name") || "",
    carer_email: searchParams.get("carer_email") || "",
    proposed_start_date: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // "Who is the care for?" only ever needs asking when this Room has no
  // linked Care Need/Placement already (see linkedPlacementId above).
  const [placements, setPlacements] = useState(null); // null = loading
  const [placementChoice, setPlacementChoice] = useState("existing_pending"); // placement_id | "new" | "existing_pending"

  useEffect(() => {
    if (linkedPlacementId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/api/care-placements/mine");
        if (!alive) return;
        const rows = data.placements || [];
        setPlacements(rows);
        setPlacementChoice(rows.length ? rows[0].placement_id : "new");
      } catch {
        if (alive) { setPlacements([]); setPlacementChoice("new"); }
      }
    })();
    return () => { alive = false; };
  }, [linkedPlacementId]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = { ...form, creator_role: "family" };
      if (linkedPlacementId) {
        payload.placement_id = linkedPlacementId;
      } else if (placementChoice && placementChoice !== "new") {
        const chosen = (placements || []).find((p) => p.placement_id === placementChoice);
        payload.placement_id = placementChoice;
        if (chosen) payload.client_label = chosen.label;
      }
      // strip empties so backend uses defaults
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      const { data } = await api.post("/api/care-rooms", payload);
      toast.success("Care Discussion Room created");
      nav(`/care-discussion-room/${data.room_id}`);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  const showNewClientLabelInput =
    (!linkedPlacementId && placementChoice === "new") ||
    (!linkedPlacementId && placements !== null && placements.length === 0);

  return (
    <div data-testid="page-cdr-new">
      <PageHero
        eyebrow="Care Discussion Room"
        title="Create a new Care Discussion Room"
        description="Each room is private to you and one carer you invite. Default expiry is 7 days."
      />
      <section className="section pt-12">
        <div className="container-jcc max-w-2xl">
          <form onSubmit={submit} className="card-base p-7 md:p-9 space-y-5" data-testid="cdr-new-form">
            {linkedPlacementId ? (
              <div className="rounded-xl border border-teal/20 bg-teal/5 px-4 py-3" data-testid="cdr-placement-linked">
                <p className="text-sm font-medium">Who is the care for?</p>
                <p className="mt-1 text-sm text-ink-soft">
                  {form.client_label ? (
                    <>Carried over from your Care Need: <strong>{form.client_label}</strong>.</>
                  ) : (
                    <>Carried over from your Care Need — you won&rsquo;t be asked again.</>
                  )}
                </p>
              </div>
            ) : placements === null ? (
              <p className="text-sm text-ink-soft" data-testid="cdr-placement-loading">Loading…</p>
            ) : placements.length > 0 ? (
              <div>
                <label className="block text-sm font-medium mb-1.5">Who is the care for? <span className="text-red-500">*</span></label>
                <select
                  value={placementChoice}
                  onChange={(e) => setPlacementChoice(e.target.value)}
                  data-testid="cdr-placement-select"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-teal focus:ring-1 focus:ring-teal outline-none"
                >
                  {placements.map((p) => (
                    <option key={p.placement_id} value={p.placement_id} data-testid={`cdr-placement-option-${p.placement_id}`}>
                      {p.label}
                    </option>
                  ))}
                  <option value="new">Someone new</option>
                </select>
                <p className="mt-1 text-xs text-ink-soft">Select who you&rsquo;ve already told us about, or add someone new.</p>
              </div>
            ) : null}

            {showNewClientLabelInput && (
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {placementChoice === "new" ? "Short name or initials for this person" : "Who is the care for?"}
                  {" "}<span className="text-red-500">*</span>
                </label>
                <input required name="client_label" value={form.client_label} onChange={onChange}
                  placeholder="e.g. Mrs J.S."
                  data-testid="cdr-client-label"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-teal focus:ring-1 focus:ring-teal outline-none" />
                <p className="mt-1 text-xs text-ink-soft">Use initials or a short label — full name is not required.</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1.5">Carer&rsquo;s name (optional)</label>
              <input name="carer_name" value={form.carer_name} onChange={onChange}
                placeholder="e.g. Alex Doe"
                data-testid="cdr-carer-name"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-teal focus:ring-1 focus:ring-teal outline-none" />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">Carer&rsquo;s email <span className="text-red-500">*</span></label>
              <input required type="email" name="carer_email" value={form.carer_email} onChange={onChange}
                placeholder="alex@example.com"
                data-testid="cdr-carer-email"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-teal focus:ring-1 focus:ring-teal outline-none" />
              <p className="mt-1 text-xs text-ink-soft">They&rsquo;ll sign in with this email using a one-time link &mdash; no password needed.</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">Proposed start date</label>
              <input type="date" name="proposed_start_date" value={form.proposed_start_date} onChange={onChange}
                data-testid="cdr-start-date"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-teal focus:ring-1 focus:ring-teal outline-none" />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">Notes (optional)</label>
              <textarea name="notes" value={form.notes} onChange={onChange} rows={4}
                maxLength={2000}
                data-testid="cdr-notes"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-teal focus:ring-1 focus:ring-teal outline-none" />
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <button type="submit" disabled={submitting} className="btn-teal" data-testid="cdr-submit">
                {submitting ? "Creating…" : "Create Care Discussion Room"}
              </button>
              <button type="button" onClick={() => nav(-1)} className="btn-outline">
                <ArrowLeft className="w-4 h-4" /> Cancel
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
